import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Headphones, Play, Radio } from "lucide-react";

const API = `${window.__BACKEND__}/api`;
const SEEN_KEY = "vdh_podcast_last_seen_id";

function formatDuration(seconds) {
  if (!seconds) return "";
  const n = parseInt(seconds, 10);
  if (Number.isNaN(n)) return "";
  const m = Math.floor(n / 60);
  const s = n % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function formatRelative(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffDays = Math.floor((now - d) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return "oggi";
    if (diffDays === 1) return "ieri";
    if (diffDays < 7) return `${diffDays} giorni fa`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} settimane fa`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} mesi fa`;
    return `${Math.floor(diffDays / 365)} anni fa`;
  } catch {
    return "";
  }
}

/**
 * Minimal podcast ticker bar.
 * - Fetches the latest Host & Furious episode
 * - Shows a small "NEW" pulse if the latest id differs from the last-seen (localStorage)
 * - Click → /servizi/podcast (marks as seen)
 */
export default function PodcastTicker() {
  const [latest, setLatest] = useState(null);
  const [isNew, setIsNew] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/podcast/latest`)
      .then((r) => {
        if (cancelled) return;
        const ep = r.data?.latest;
        if (!ep) return;
        setLatest(ep);
        const seen = localStorage.getItem(SEEN_KEY);
        setIsNew(seen !== ep.id);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const markSeen = () => {
    if (latest?.id) localStorage.setItem(SEEN_KEY, latest.id);
    setIsNew(false);
  };

  if (!latest) return null;

  return (
    <section
      className="relative px-5 md:px-8 py-3 border-y border-white/5"
      data-testid="podcast-ticker"
    >
      <Link
        to="/servizi/podcast"
        onClick={markSeen}
        className="group max-w-7xl mx-auto flex items-center gap-3 md:gap-4"
      >
        {/* Left: icon + label */}
        <div className="flex items-center gap-2 flex-none">
          <div className="relative w-8 h-8 md:w-9 md:h-9 rounded-full bg-[var(--vdh-pink)]/15 border border-[var(--vdh-pink)]/40 flex items-center justify-center">
            <Radio size={14} className="text-[var(--vdh-pink)]" />
            <AnimatePresence>
              {isNew && (
                <motion.span
                  initial={{ scale: 0 }}
                  animate={{ scale: [1, 1.25, 1] }}
                  exit={{ scale: 0 }}
                  transition={{ duration: 1.2, repeat: Infinity }}
                  className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-red-500 border border-black"
                  data-testid="podcast-new-dot"
                />
              )}
            </AnimatePresence>
          </div>
          <div className="hidden sm:flex flex-col leading-tight">
            <span className="text-[9px] font-tech uppercase tracking-[0.25em] text-white/45">
              Host &amp; Furious · Podcast
            </span>
            <span className="text-[10px] text-white/35 font-tech">
              {isNew ? "Nuovo episodio" : "Ultimo episodio"}
            </span>
          </div>
        </div>

        {/* Middle: scrolling title */}
        <div className="flex-1 min-w-0 overflow-hidden">
          <div className="flex items-center gap-3 text-sm">
            {isNew && (
              <span className="flex-none text-[9px] font-tech uppercase tracking-widest px-1.5 py-0.5 rounded bg-red-500/20 border border-red-500/40 text-red-300">
                NEW
              </span>
            )}
            <span className="truncate text-white/85 font-medium">
              {latest.title}
            </span>
            <span className="hidden md:inline text-white/35 text-xs flex-none">
              · {formatRelative(latest.pub_date)}
              {latest.duration ? ` · ${formatDuration(latest.duration)}` : ""}
            </span>
          </div>
        </div>

        {/* Right: CTA */}
        <div className="flex-none inline-flex items-center gap-2 text-xs font-tech uppercase tracking-widest text-white/55 group-hover:text-[var(--vdh-yellow)] transition-colors">
          <Play size={13} />
          <span className="hidden sm:inline">Ascolta</span>
        </div>
      </Link>
    </section>
  );
}
